/**
 * DemoTodayBoard - Primary visual proof component for landing page
 * 
 * Shows a static snapshot of the TodayBoard with proper sectioned table structure:
 * - 2 Needs Attention orders
 * - 3 Today orders
 * - Embedded LivePulse strip with clear separation
 * 
 * Mirrors the real TodayBoard/NeedsAttentionTable/TodayTable structure
 * 
 * GUARDRAIL: This component uses only static demo data.
 * No imports from hooks/, features/, or services/.
 */

import { Clock, Calendar, ChevronDown, Package } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { DemoLivePulse } from "./DemoLivePulse";
import { RoleIcon, type LogRole } from "@/components/ui/RoleIcon";
import { 
  getDemoOrders,
  STATUS_COLORS, 
  type DemoOrder 
} from "@/lib/demoData";
import { useLanguage } from "@/contexts/LanguageContext";
import { useMemo } from "react";
import { getLastLogEntry } from "@/lib/deliveryLogFormat";

// getLastLogEntry is now imported from @/lib/deliveryLogFormat

// Format products for display
function formatProducts(parsedItems: string | null): string {
  if (!parsedItems) return "";
  try {
    const items = JSON.parse(parsedItems);
    if (!Array.isArray(items) || items.length === 0) return "";
    const names = items.map((item: { name?: string }) => item.name || "").filter(Boolean);
    const joined = names.join(", ");
    return joined.length > 20 ? joined.slice(0, 20) + "..." : joined;
  } catch {
    return "";
  }
}

interface SectionHeaderProps {
  icon: React.ElementType;
  title: string;
  count: number;
  variant?: "attention" | "today";
}

function SectionHeader({ icon: Icon, title, count, variant = "today" }: SectionHeaderProps) {
  return (
    <div className={cn(
      "flex items-center justify-between gap-3 py-2 px-3",
      "border-b border-border/30"
    )}>
      <div className="flex items-center gap-2">
        <Icon className={cn(
          "h-3.5 w-3.5",
          variant === "attention" ? "text-amber-600/80" : "text-muted-foreground"
        )} />
        <span className="font-medium text-sm text-foreground">{title}</span>
        <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-5 font-normal">
          {count}
        </Badge>
      </div>
      <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
    </div>
  );
}

// Needs Attention row - shows alert reason first, Status at end
function AttentionTableRow({ order, statusLabel }: { order: DemoOrder; statusLabel: string }) {
  const statusStyle = STATUS_COLORS[order.order_status] || STATUS_COLORS["New"];
  const products = formatProducts(order.parsed_items);
  const alertReason = order.comments || "";
  
  // Determine dot color based on order status severity
  const isCritical = ["Delivery Failed", "Cancelled"].includes(order.order_status);
  const dotColor = isCritical ? "bg-red-500" : "bg-amber-500";
  
  return (
    <TableRow className="hover:bg-muted/30 transition-colors">
      {/* Alert Reason Column (first) - same width as Status in Today table */}
      <TableCell className="w-[100px] py-2 px-2">
        <div className="flex items-center gap-1.5">
          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${dotColor}`} />
          <span className="text-[11px] text-muted-foreground font-medium truncate">
            {alertReason.length > 12 ? alertReason.slice(0, 12) + "..." : alertReason}
          </span>
        </div>
      </TableCell>
      
      {/* Session Column */}
      <TableCell className="w-[60px] py-2 px-2 text-center">
        {order.session ? (
          <div className="flex items-center gap-1 justify-center">
            <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
            <span className="text-[10px] text-primary font-medium truncate max-w-[35px]">
              {order.session.length > 8 ? order.session.slice(0, 8) + "..." : order.session}
            </span>
            <ChevronDown className="h-2.5 w-2.5 text-primary/50" />
          </div>
        ) : (
          <span className="text-[10px] text-muted-foreground/50">—</span>
        )}
      </TableCell>
      
      {/* Customer Column */}
      <TableCell className="w-[140px] py-2 px-2">
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-1.5">
            <span className="font-medium text-xs text-foreground truncate">
              {order.customer_name}
            </span>
            {order.context_tag && (
              <span className="text-[9px] px-1 py-0.5 rounded bg-primary/10 text-primary font-medium whitespace-nowrap">
                {order.context_tag}
              </span>
            )}
          </div>
          <span className="text-[10px] text-muted-foreground">
            {order.order_number}
          </span>
        </div>
      </TableCell>
      
      {/* Products/Price Column */}
      <TableCell className="w-[100px] py-2 px-2">
        <div className="flex flex-col gap-0.5">
          {products && (
            <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
              <Package className="h-2.5 w-2.5 flex-shrink-0" />
              <span className="truncate">{products}</span>
            </div>
          )}
          {order.total_price && (
            <span className="text-xs font-medium text-foreground">
              {order.total_price}₾
            </span>
          )}
        </div>
      </TableCell>
      
      {/* Status Column (last) - same width as Log in Today table */}
      <TableCell className="w-[100px] py-2 px-2">
        <span className={cn(
          "inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium",
          statusStyle.bg,
          statusStyle.text
        )}>
          {statusLabel}
        </span>
      </TableCell>
    </TableRow>
  );
}

// Today row - shows status first
function TodayTableRow({ order, statusLabel }: { order: DemoOrder; statusLabel: string }) {
  const statusStyle = STATUS_COLORS[order.order_status] || STATUS_COLORS["New"];
  const products = formatProducts(order.parsed_items);
  const lastLog = getLastLogEntry(order.internal_notes);
  
  return (
    <TableRow className={cn(
      "hover:bg-muted/30 transition-colors",
      statusStyle.rowBg
    )}>
      {/* Status Column - same width as Alert Reason in Needs Attention table */}
      <TableCell className="w-[100px] py-2 px-2">
        <span className={cn(
          "inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium",
          statusStyle.bg,
          statusStyle.text
        )}>
          {statusLabel}
        </span>
      </TableCell>
      
      {/* Session Column */}
      <TableCell className="w-[60px] py-2 px-2 text-center">
        {order.session ? (
          <div className="flex items-center gap-1 justify-center">
            <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
            <span className="text-[10px] text-primary font-medium truncate max-w-[35px]">
              {order.session.length > 8 ? order.session.slice(0, 8) + "..." : order.session}
            </span>
            <ChevronDown className="h-2.5 w-2.5 text-primary/50" />
          </div>
        ) : (
          <span className="text-[10px] text-muted-foreground/50">—</span>
        )}
      </TableCell>
      
      {/* Customer Column */}
      <TableCell className="w-[140px] py-2 px-2">
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-1.5">
            <span className="font-medium text-xs text-foreground truncate">
              {order.customer_name}
            </span>
            {order.context_tag && (
              <span className="text-[9px] px-1 py-0.5 rounded bg-primary/10 text-primary font-medium whitespace-nowrap">
                {order.context_tag}
              </span>
            )}
          </div>
          <span className="text-[10px] text-muted-foreground">
            {order.order_number}
          </span>
        </div>
      </TableCell>
      
      {/* Products/Price Column */}
      <TableCell className="w-[100px] py-2 px-2">
        <div className="flex flex-col gap-0.5">
          {products && (
            <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
              <Package className="h-2.5 w-2.5 flex-shrink-0" />
              <span className="truncate">{products}</span>
            </div>
          )}
          {order.total_price && (
            <span className="text-xs font-medium text-foreground">
              {order.total_price}₾
            </span>
          )}
        </div>
      </TableCell>
      
      {/* Delivery Log Column - same width as Status in Needs Attention table */}
      <TableCell className="w-[100px] py-2 px-2">
        {lastLog ? (
          <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
            <RoleIcon role={lastLog.role} size="sm" className="flex-shrink-0" />
            <span className="truncate">{lastLog.message}</span>
          </div>
        ) : (
          <span className="text-[10px] text-muted-foreground">—</span>
        )}
      </TableCell>
    </TableRow>
  );
}

export function DemoTodayBoard() {
  const { t } = useLanguage();
  
  // Get translated demo data
  const demoOrders = useMemo(() => getDemoOrders(t), [t]);
  const needsAttentionOrders = useMemo(() => demoOrders.filter(o => !o.actioned_today_at), [demoOrders]);
  const todayOrders = useMemo(() => demoOrders.filter(o => o.actioned_today_at).slice(0, 3), [demoOrders]);
  
  // Get translated status label
  const getStatusLabel = (status: string) => {
    return t(`landing.demo.statuses.${status}`) || status;
  };
  
  return (
    <div className="space-y-3">
      {/* Needs Attention Section - SEPARATE CONTAINER */}
      <div className="overflow-hidden border border-border/40 rounded-xl bg-card/50 shadow-sm">
        <div className="pt-2">
          <SectionHeader 
            icon={Clock} 
            title={t("landing.demo.needsAttention")} 
            count={needsAttentionOrders.length}
            variant="attention"
          />
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-[100px] h-7 text-[10px] font-normal text-muted-foreground">{t("landing.demo.tableHeaders.note")}</TableHead>
                  <TableHead className="w-[60px] h-7 text-[10px] font-normal text-muted-foreground text-center">{t("landing.demo.tableHeaders.session")}</TableHead>
                  <TableHead className="w-[140px] h-7 text-[10px] font-normal text-muted-foreground">{t("landing.demo.tableHeaders.customer")}</TableHead>
                  <TableHead className="w-[100px] h-7 text-[10px] font-normal text-muted-foreground">{t("landing.demo.tableHeaders.product")}</TableHead>
                  <TableHead className="w-[100px] h-7 text-[10px] font-normal text-muted-foreground">{t("landing.demo.tableHeaders.status")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {needsAttentionOrders.map(order => (
                  <AttentionTableRow 
                    key={order.id} 
                    order={order} 
                    statusLabel={getStatusLabel(order.order_status)}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
      
      {/* Today Section - SEPARATE CONTAINER */}
      <div className="overflow-hidden border border-border/40 rounded-xl bg-card/50 shadow-sm">
        <div className="pt-2">
          <SectionHeader 
            icon={Calendar} 
            title={t("landing.demo.today")} 
            count={todayOrders.length}
          />
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-[100px] h-7 text-[10px] font-normal text-muted-foreground">{t("landing.demo.tableHeaders.status")}</TableHead>
                  <TableHead className="w-[60px] h-7 text-[10px] font-normal text-muted-foreground text-center">{t("landing.demo.tableHeaders.session")}</TableHead>
                  <TableHead className="w-[140px] h-7 text-[10px] font-normal text-muted-foreground">{t("landing.demo.tableHeaders.customer")}</TableHead>
                  <TableHead className="w-[100px] h-7 text-[10px] font-normal text-muted-foreground">{t("landing.demo.tableHeaders.product")}</TableHead>
                  <TableHead className="w-[100px] h-7 text-[10px] font-normal text-muted-foreground">{t("landing.demo.tableHeaders.log")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {todayOrders.map(order => (
                  <TodayTableRow 
                    key={order.id} 
                    order={order}
                    statusLabel={getStatusLabel(order.order_status)}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
      
      {/* Live Pulse - EMBEDDED AT BOTTOM */}
      <DemoLivePulse embedded />
    </div>
  );
}